import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { Subject, merge } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { APIListingResponseInterface } from '@app/core/_common/model';
import { ProductTypes } from '@app/core/_constant/product-type.constants';
import { RFQ } from '@app/core/quotes';
import { UnitMeasureService } from '@app/core/unit-measurement/services/unit-measure/unit-measure.service';
import { UnitMeasureInterface } from '@app/modules/unit-measurement/interfaces';
import { formatQuantity } from '@app/shared/utils/number.utils';

@Component({
  selector: 'cnc-rfq-kit-items',
  templateUrl: './rfq-kit-items.component.html',
  styleUrls: ['./rfq-kit-items.component.scss'],
})
export class RfqKitItemsComponent implements OnInit, OnDestroy {
  @Input() controlName :string;
  @Input() formGroup :UntypedFormGroup;
  @Input() items :any[];

  totalSellingPrice :number = 0;
  totalOriginalPrice :number = 0;

  unitOfMeasures :UnitMeasureInterface[] = [];
  tempUomPerClassId :UnitMeasureInterface[] = [];
  productTypes = ProductTypes;

  changesUnsubscribe = new Subject();
  private onDestroy$ = new Subject();

  constructor(
    private fb :UntypedFormBuilder,
    private unitMeasureService :UnitMeasureService,
  ) {}

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  ngOnInit() :void {
    this.initData();
    this.detectChanges();
  }

  addItem(item ?:UntypedFormGroup) :void {
    if (item) {
      this.kitItems.push(item);
    } else {
      this.kitItems.push(
        this.fb.group({
          id: [null],
          sku: [null],
          productId: [null],
          quantity: [1],
          price: [0],
          originalPrice: [0],
          lineTotal: [0],
          uomId: [null],
        }),
      );
    }

    this.detectChanges();
  }

  addSelectedItem(idx :number, data :any) :void {
    if (!data) {
      this.kitItems.at(idx).patchValue({
        sku: null,
        productId: null,
        originalPrice: null,
        price: null,
        sellable: null,
      });

      return;
    }

    this.kitItems.at(idx).patchValue({
      productId: data.id,
      originalPrice: data.unit_price,
      price: data.unit_price,
      sku: {
        sku: data.sku,
        product_name: data.product_name,
        id: data.id,
        unit_of_measures: data.unit_of_measures,
        sellable: data.sellable,
      },
    });

    this.updateLineItemUom(idx);
  }

  showTooltip(item) :string {
    return item?.value?.rfq?.product_name || item?.value?.product_name;
  }

  addMultipleItems(rows :number) :void {
    for (let item = 0; item < rows; item++) {
      this.addItem();
    }
  }

  removeItem(idx :number) :void {
    this.kitItems.removeAt(idx);
  }

  get kitItems() :UntypedFormArray {
    return this.formGroup.get(this.controlName) as UntypedFormArray;
  }

  private initData() :void {
    if (this.items.length) {
      this.items.forEach((item, idx) => {
        const { id, product, original_price, price, quantity, uom_id, product_id } =
          item;

        this.addItem(
          this.fb.group({
            id,
            price,
            quantity: formatQuantity(quantity),
            originalPrice: original_price,
            productId: product_id,
            uomId: uom_id,
            lineTotal: quantity * price,
            sku: {
              sku: product.internal_sku,
              product_name: product.product_name,
              id: product.id,
              unit_of_measures: product.unit_of_measures,
              unit_class_id: product.unit_class_id,
            },
          }),
        );

        if (!product.unit_of_measures) {
          this.getSkuUnitOfMeasures(product);
        }

        this.updateLineItemUom(idx);
      });

      this.computeTotal();
    } else {
      this.addItem();
    }
  }

  private computeTotal() :void {
    this.totalOriginalPrice = 0;
    this.totalSellingPrice = 0;
    this.kitItems.value.forEach((item) => {
      this.totalSellingPrice += item.quantity * item.price;
      this.totalOriginalPrice += item.quantity * item.originalPrice;
    });
  }

  private computeLineTotal(idx :number, data :any) :void {
    const lineTotal = data.quantity * data.price;
    this.kitItems.at(idx).patchValue(
      {
        lineTotal: lineTotal,
      },
      { emitEvent: false },
    );
    this.computeTotal();
  }

  private detectChanges() :void {
    this.changesUnsubscribe.next(null);

    merge(
      ...this.kitItems.controls.map((control :AbstractControl, index :number) =>
        control.valueChanges.pipe(
          takeUntil(this.changesUnsubscribe),
          map((value) => ({
            rowIndex: index,
            control: control,
            data: value,
          })),
        ),
      ),
    ).subscribe((changes) => {
      const { rowIndex, data } = changes;
      this.computeLineTotal(rowIndex, data);
    });
  }

  private updateLineItemUom(idx :number) :void {
    const item = this.kitItems.at(idx).value;

    if (item.sku?.unit_of_measures) {
      const uomValues = this.getOrderedUOMValues(item);
      this.kitItems.at(idx).patchValue(uomValues);
    }
  }

  private getSkuUnitOfMeasures(sku :RFQ) :void {
    if (this.tempUomPerClassId[sku.unit_class_id]) {
      this.unitOfMeasures[sku.id] = this.tempUomPerClassId[sku.unit_class_id];
    } else {
      this.unitMeasureService
        .getAsync({ unitClassId: sku.unit_class_id })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((response :any) => {
          this.tempUomPerClassId[sku.unit_class_id] = response.data;
          this.unitOfMeasures[sku.id] = response.data;
        });
    }
  }

  private getOrderedUOMValues(lineItem :any) :any {
    if (lineItem.sku.unit_of_measures) {
      this.unitOfMeasures[lineItem.sku.id] = lineItem.sku.unit_of_measures;
    } else {
      this.getSkuUnitOfMeasures(lineItem.sku);
    }
    if (lineItem.uomId) {
      return {
        uomId: lineItem.uomId,
      };
    }
    const baseUom :any = lineItem.sku.unit_of_measures.find(
      (uomRow :any) => uomRow.base_unit === 1,
    );

    return {
      uomId: baseUom?.id || null,
    };
  }
}
